// The recording pill shown during voice input, beside the mic.
// Copied from frontend/src/components/agent/RecordingOverlay.tsx.
// Generic — no Platinum-specific dependencies.
// See ../../docs/90-provenance-map.md.
//
// It never unmounts itself: the toolbar owns its entrance and exit (a slide
// and a fade), so while `isRecording` is false it keeps drawing the last clock
// and waveform for the fade-out. Returning null here made the pill vanish the
// instant Done was pressed, leaving the toolbar to fade an empty box.

import React, { useCallback, useRef, useState } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { HAIRLINE, PILL_RADIUS } from './surface.js'

/** The pill's height: the model picker's, so the row reads as one family. */
export const PILL_HEIGHT = 36

interface RecordingOverlayProps {
  stream: MediaStream | null
  isRecording: boolean
  onStop: () => void
  onCancel: () => void
  compact?: boolean
}

export default function RecordingOverlay({ stream, isRecording, onStop, onCancel }: RecordingOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const animFrameRef = useRef<number>(0)
  const timerFrameRef = useRef<number>(0)
  const startTimeRef = useRef<number>(0)
  const [elapsed, setElapsed] = useState(0)
  const initializedRef = useRef(false)

  const cleanup = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    if (timerFrameRef.current) cancelAnimationFrame(timerFrameRef.current)
    animFrameRef.current = 0
    timerFrameRef.current = 0
    sourceRef.current?.disconnect()
    sourceRef.current = null
    audioCtxRef.current?.close()
    audioCtxRef.current = null
    analyserRef.current = null
    initializedRef.current = false
  }, [])

  const handleStop = useCallback(() => {
    cleanup()
    onStop()
  }, [cleanup, onStop])

  const handleCancel = useCallback(() => {
    cleanup()
    onCancel()
  }, [cleanup, onCancel])

  // Initialize audio analyser and start animation when stream is available
  if (stream && isRecording && !initializedRef.current) {
    initializedRef.current = true
    startTimeRef.current = Date.now()
    // A fresh recording starts from 0:00; the previous clock stayed on screen
    // through the last fade-out on purpose.
    setElapsed(0)

    const audioCtx = new AudioContext()
    audioCtxRef.current = audioCtx
    if (audioCtx.state === 'suspended') {
      audioCtx.resume()
    }
    const analyser = audioCtx.createAnalyser()
    analyser.fftSize = 128
    analyser.smoothingTimeConstant = 0.6
    analyserRef.current = analyser
    const source = audioCtx.createMediaStreamSource(stream)
    source.connect(analyser)
    sourceRef.current = source

    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    const draw = () => {
      const canvas = canvasRef.current
      if (!canvas) {
        animFrameRef.current = requestAnimationFrame(draw)
        return
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      analyser.getByteFrequencyData(dataArray)
      const w = canvas.width
      const h = canvas.height
      ctx.clearRect(0, 0, w, h)

      const barCount = dataArray.length
      const gap = 2
      const barWidth = Math.max(2, (w - gap * (barCount - 1)) / barCount)

      for (let i = 0; i < barCount; i++) {
        const val = dataArray[i] / 255
        const barHeight = Math.max(2, val * h * 0.9)
        const x = i * (barWidth + gap)
        const y = (h - barHeight) / 2

        ctx.fillStyle = `rgba(59, 130, 246, ${0.4 + val * 0.6})`
        ctx.beginPath()
        ctx.roundRect(x, y, barWidth, barHeight, 1)
        ctx.fill()
      }

      animFrameRef.current = requestAnimationFrame(draw)
    }
    draw()

    const tick = () => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000))
      timerFrameRef.current = requestAnimationFrame(tick)
    }
    tick()
  }

  const minutes = Math.floor(elapsed / 60)
  const seconds = elapsed % 60
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`

  // The recording control is a pill in the same family as the model picker
  // beside it (Adam, 14 Sep): PILL_HEIGHT tall, round both ends, the
  // hairline its only edge, white ground. It stands beside the mic in the
  // controls row rather than spanning the card, so nothing hangs over the
  // messages. A red dot and the clock say what is happening, a short
  // waveform says the mic hears you, Done and a cross end it.
  return (
    <Box
      data-testid="recording-pill"
      role="group"
      aria-label={`Recording ${timeStr}`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
        height: PILL_HEIGHT, pl: '14px', pr: '6px',
        borderRadius: PILL_RADIUS, border: HAIRLINE, bgcolor: 'background.paper',
        // The composer's controls row turns every icon button into a 40px
        // white disc; the cross inside this pill is not one of those.
        '&& .MuiIconButton-root': { width: 26, height: 26, border: 'none', bgcolor: 'transparent' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0,
          animation: 'recDotPulse 1s ease-in-out infinite',
          '@keyframes recDotPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {timeStr}
      </Typography>
      <canvas ref={canvasRef} width={180} height={22} style={{ width: 90, height: 22, flexShrink: 0 }} />
      <Button
        variant="contained"
        color="info"
        size="small"
        onClick={handleStop}
        disableElevation
        sx={{ borderRadius: PILL_RADIUS, textTransform: 'none', fontSize: 12, fontWeight: 600, minWidth: 0, height: 26, px: 1.5, py: 0, lineHeight: 1 }}
      >
        Done
      </Button>
      <IconButton size="small" onClick={handleCancel} aria-label="Cancel recording" sx={{ width: 26, height: 26, color: 'text.secondary' }}>
        <CloseIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  )
}
